import { useParams, useNavigate } from "react-router-dom"
import Grid from "@mui/material/Grid"
import DashboardLayout from "examples/LayoutContainers/DashboardLayout"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import SectionCard from "./components/SectionCard"
import StatusBadge from "./components/StatusBadge"
import ScopedDataTable from "./components/ScopedDataTable"
import AttachmentSlot from "./components/AttachmentSlot"
import { formatAmount } from "./constants"
import { KYC_STATUS } from "./status"
import {
  useGetSupplierDetailQuery,
  useSetSupplierKycStatusMutation,
} from "shared/redux/endpoints/procurementV2"

const Field = ({ label, value }) => (
  <Grid item xs={12} md={4}>
    <MDTypography variant="caption" color="text">
      {label}
    </MDTypography>
    <MDTypography variant="button" fontWeight="medium" display="block">
      {value}
    </MDTypography>
  </Grid>
)

const SupplierDetailContainer = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: supplier, isFetching } = useGetSupplierDetailQuery(id, {
    skip: !id,
  })
  const [setKyc, { isLoading: updating }] = useSetSupplierKycStatusMutation()

  if (isFetching) {
    return (
      <DashboardLayout>
        <MDBox p={3}>
          <MDTypography color="text">Loading…</MDTypography>
        </MDBox>
      </DashboardLayout>
    )
  }

  if (!supplier) {
    return (
      <DashboardLayout>
        <MDBox p={3}>
          <MDTypography color="error">Supplier not found.</MDTypography>
        </MDBox>
      </DashboardLayout>
    )
  }

  const proposalColumns = [
    { Header: "OR Code", accessor: "orCode" },
    {
      Header: "Quote",
      accessor: "quote",
      Cell: ({ value }) => formatAmount(value),
    },
    {
      Header: "Proposed",
      accessor: "proposed",
      Cell: ({ value }) => (value ? "Yes" : "No"),
    },
    { Header: "Date", accessor: "date" },
  ]

  return (
    <DashboardLayout>
      <MDBox p={3}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <MDBox>
            <MDTypography variant="h4">{supplier.name}</MDTypography>
            <MDTypography variant="caption" color="text">
              Procurement › Suppliers › Detail
            </MDTypography>
          </MDBox>
          <StatusBadge status={supplier.kycStatus} />
        </MDBox>

        {supplier.pendingApproval && (
          <SectionCard
            title="KYC Review"
            subtitle="This supplier is awaiting your review."
            icon="how_to_reg"
            p={2.5}
            mb={3}
          >
            <MDBox display="flex" gap={1}>
              <MDButton
                variant="gradient"
                color="success"
                disabled={updating}
                onClick={() =>
                  setKyc({ id, kycStatus: KYC_STATUS.VERIFIED }).catch(() => null)
                }
              >
                Approve
              </MDButton>
              <MDButton
                variant="outlined"
                color="error"
                disabled={updating}
                onClick={() =>
                  setKyc({ id, kycStatus: KYC_STATUS.REJECTED }).catch(() => null)
                }
              >
                Reject
              </MDButton>
            </MDBox>
          </SectionCard>
        )}

        <SectionCard title="Supplier Information" icon="store">
          <Grid container spacing={2}>
            <Field label="Tax Code" value={supplier.taxCode} />
            <Field label="Region" value={supplier.region} />
            <Field label="Category" value={supplier.categoryName} />
            <Field
              label="Performance Score"
              value={`${supplier.performanceScore}/100`}
            />
            <Field label="Active" value={supplier.active ? "Yes" : "No"} />
            <Field label="Last Used" value={supplier.lastUsed} />
          </Grid>
        </SectionCard>

        <SectionCard title="Proposals History" icon="history">
          <ScopedDataTable
            columns={proposalColumns}
            rows={supplier.proposals}
            emptyText="No proposal history."
          />
        </SectionCard>

        <SectionCard title="Documents" icon="folder">
          {(supplier.documents || []).map((d) => (
            <AttachmentSlot
              key={d.name}
              label={d.name}
              docType={d.type}
              file={{ name: d.name }}
              disabled
            />
          ))}
        </SectionCard>

        <MDBox display="flex" justifyContent="flex-end">
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => navigate("/procurement/suppliers")}
          >
            Back
          </MDButton>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  )
}

export default SupplierDetailContainer
